import type { TestProjectJobDto, TestProjectsResetDto } from '@nsb/core';
import { dateWords } from '../../days.ts';

/**
 * The words of Settings → Development tools → Test projects (TestProjects.tsx), out of the component so they are held
 * by a test of their own: what resetting will do, where a reset stands, and how each documented job went.
 */

const many = (n: number, one: string, more: string) => `${n} ${n === 1 ? one : more}`;
/** Words that end their sentence once, whether or not they came with a full stop. */
const ended = (words: string) => (/[.!?…]$/.test(words.trim()) ? words.trim() : `${words.trim()}.`);

/** Asked once, before it starts: the test projects it deletes, by count, and the jobs it makes. */
export function resetPrompt(testProjects: number, jobs: number): string {
  const make = `make the ${many(jobs, 'documented job', 'documented jobs')} again`;
  if (testProjects === 0) return `There are no test projects to delete. ${make[0]!.toUpperCase()}${make.slice(1)}?`;
  return `Delete ${testProjects === 1 ? 'the one test project' : `all ${testProjects} test projects`} on this machine for good, and ${make}?`;
}

/** Where a reset stands, in a sentence: under way, done, or (the API stopped in the middle of it) not through. */
export function resetLine(r: TestProjectsResetDto): string {
  const made = r.jobs.filter((j) => j.state === 'made').length;
  const failed = r.jobs.filter((j) => j.state === 'failed').length;
  const by = r.by ? `, started by ${r.by}` : '';
  if (!r.running && !r.finishedAt) return `The reset started ${dateWords(r.startedAt, 'time')}${by} did not finish.`;
  if (r.running) return `Under way since ${dateWords(r.startedAt, 'time')}${by}: ${many(r.deleted, 'test project', 'test projects')} deleted, ${made} of ${r.jobs.length} made so far.`;
  return `Done ${dateWords(r.finishedAt, 'time')}${by}: ${many(r.deleted, 'test project', 'test projects')} deleted, ${made} of ${r.jobs.length} made${failed ? `, ${failed} failed` : ''}.`;
}

/** How one job went, in a word or two, never colour alone. */
export function jobWord(j: TestProjectJobDto): string {
  if (j.state === 'waiting') return 'Waiting';
  if (j.state === 'playing') return 'Being made';
  if (j.state === 'failed') return 'Failed';
  return j.differences.length ? 'Made, not as recorded' : 'Made';
}

/**
 * A job that failed: the call of its recipe that was refused (with what the API answered) or could not be made (a step
 * it names that the job does not have), and why; or the play stopped by something no call did.
 */
export function failedWords(f: NonNullable<TestProjectJobDto['failed']>): string {
  const why = ended(f.refusal);
  if (!f.method) return `The play stopped: ${why} What it had made was deleted again.`;
  const how = f.status ? `was refused (${f.status})` : 'could not be made';
  if (f.call === 0) return `Making the project ${how}: ${why}`;
  return `Call ${f.call} of its recipe, ${f.method} ${f.path}, ${how}: ${why} What it had made was deleted again.`;
}

/** A job made whose end differs from its recipe's record: how many ways, before the list. */
export function differencesWords(j: TestProjectJobDto): string {
  const n = j.differences.filter((d) => !/^and \d+ more$/.test(d)).length;
  const more = j.differences.find((d) => /^and \d+ more$/.test(d));
  const total = n + (more ? Number(/\d+/.exec(more)![0]) : 0);
  return `It ends ${many(total, 'way', 'ways')} unlike the record of the job:`;
}

/**
 * What `npm run dev` does by itself (scripts/dev.ts; Jason, 2026-10-08: "i keep not seeing the 13 test projects, so
 * those really should be added back every time the dev server is wiped"): said under what resetting does.
 */
export const ADDED_BY_ITSELF = 'Each time npm run dev starts the API, any documented job missing here is made again by itself, and the rest are left as they are.';
